import Indicator from "@/components/shared/Indicator";
import { IntegrationOrderTypeColorMap } from "@/modal/integration/order/const";
import {
  Cube2BOrder,
  Cube2COrder,
  DialogRef,
  IntegrationOrderType,
} from "@/types";
import { Alert, Button, Modal, Select, Spin, Tag } from "antd";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { getAssociatedCubeOrder, useSearchCubeOrders } from "./helper";
import { useDebounce } from "@/hooks/shared/debounce";
import { updateOrderAssociatedCubeId } from "@/apis/integration/order";
import { useGlobalContext } from "@/hooks/context";

const OrderModifyCube = forwardRef<
  DialogRef,
  {
    className?: string;
    updateCb?: () => void;
  }
>((props, ref) => {
  const { className, updateCb } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { hintPrompt } = useGlobalContext();
  const [orderId, setOrderId] = useState<number | undefined>(undefined);
  const [customerOrderNo, setCustomerOrderNo] = useState<string | undefined>(
    undefined,
  );
  const [orderType, setOrderType] = useState<IntegrationOrderType | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const [cubeOrder, setCubeOrder] = useState<Cube2BOrder | Cube2COrder | null>(
    null,
  );

  const [searchContent, setSearchContent] = useState<string | undefined>(
    undefined,
  );
  const debouncedSearchContent = useDebounce(searchContent, 500);

  const [selectedCubeOrderId, setSelectedCubeOrderId] = useState<
    string | undefined
  >(undefined);

  const { data: filteredCubeOrders, isFetching: isFetchingFilteredCubeOrders } =
    useSearchCubeOrders(orderType!, debouncedSearchContent);

  const selectOptions = useMemo(() => {
    return filteredCubeOrders?.map((order) => ({
      label: <span title={order.label}>{order.label}</span>,
      value: order.value,
    }));
  }, [filteredCubeOrders]);

  const currentCubeOrderNo = useMemo(() => {
    return cubeOrder
      ? orderType === IntegrationOrderType.Business
        ? (cubeOrder as Cube2BOrder).deliverNumber
        : orderType === IntegrationOrderType.Customer
          ? (cubeOrder as Cube2COrder).instruction.instructionNumber
          : undefined
      : undefined;
  }, [cubeOrder, orderType]);

  const cubeUserCode = useMemo(() => {
    return cubeOrder
      ? orderType === IntegrationOrderType.Business
        ? (cubeOrder as Cube2BOrder).userCode
        : orderType === IntegrationOrderType.Customer
          ? (cubeOrder as Cube2COrder).userInfo.userCode
          : undefined
      : undefined;
  }, [cubeOrder, orderType]);

  const openDialog = useCallback((props: unknown) => {
    setLoading(true);
    const { orderId, type, customerOrderNo } = props as {
      orderId: number;
      type: IntegrationOrderType;
      customerOrderNo: string;
    };
    setOrderId(orderId);
    setCustomerOrderNo(customerOrderNo);
    setOrderType(type);
    getAssociatedCubeOrder(customerOrderNo, type)
      .then((res) => {
        if (res) {
          setCubeOrder(res);
          if (type === IntegrationOrderType.Business) {
            const cubeOrderId = (res as Cube2BOrder).boxStockDeliverId;
            const cubeUserCode = (res as Cube2BOrder).userCode;
            setSelectedCubeOrderId(`${cubeUserCode}@${cubeOrderId}`);
          } else if (type === IntegrationOrderType.Customer) {
            const cubeUserCode = (res as Cube2COrder).userInfo.userCode;
            const cubeOrderId = (res as Cube2COrder).instruction.instructionId;
            setSelectedCubeOrderId(`${cubeUserCode}@${cubeOrderId}`);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
    setIsModalOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsModalOpen(false);
    setOrderId(undefined);
    setCustomerOrderNo(undefined);
    setOrderType(undefined);
    setCubeOrder(null);
    setSelectedCubeOrderId(undefined);
    setSearchContent(undefined);
  }, []);

  const changeOrderAssociatedCubeId = useCallback(
    (
      orderId: number | undefined,
      cubeOrderId: string | undefined,
      cubeUserCode: string | undefined,
    ) => {
      if (orderId == null || cubeOrderId == null || cubeUserCode == null)
        return;
      setLoading(true);
      updateOrderAssociatedCubeId(orderId, { cubeOrderId, cubeUserCode })
        .then(() => {
          hintPrompt(
            "success",
            "Updated successfully",
            "The order associated cube order has been updated successfully",
          );
          closeDialog();
          updateCb?.();
        })
        .catch((e) => {
          hintPrompt("error", "Failed to update", e?.msg ?? "Unknown error");
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [hintPrompt, closeDialog, updateCb],
  );

  useImperativeHandle(ref, () => ({
    showModal: openDialog,
    hideModal: closeDialog,
  }));

  return (
    <Modal
      className={className}
      title="Modify Associated Cube Order"
      open={isModalOpen}
      onCancel={closeDialog}
      footer={null}
      maskClosable={false}
      loading={loading}
      closeIcon={null}
    >
      <div>
        <Indicator label="Customer Order No" />
        <div className="flex items-center gap-2">
          <Tag color={IntegrationOrderTypeColorMap[orderType!]}>
            {orderType}
          </Tag>
          <span>{customerOrderNo ?? "N/A"}</span>
        </div>
      </div>
      <div>
        <Indicator label="Cube Associated Order" />
        <div className="flex items-center gap-2 mb-2">
          <Tag color="blue">{cubeUserCode ?? "N/A"}</Tag>
          <span>{currentCubeOrderNo}</span>
        </div>
        {selectedCubeOrderId == null && (
          <Alert
            className="!mb-2"
            message="Please select a cube order"
            type="warning"
            showIcon
          />
        )}
        {selectedCubeOrderId != null &&
          cubeUserCode != null &&
          !selectedCubeOrderId.startsWith(cubeUserCode) && (
            <Alert
              className="!mb-2"
              message={`Selected cube order belongs to user ${selectedCubeOrderId.split("@")[0]},
               not match the user ${cubeUserCode}`}
              type="error"
              showIcon
            />
          )}
        <Select
          loading={isFetchingFilteredCubeOrders}
          className="w-full"
          showSearch
          allowClear
          options={selectOptions}
          filterOption={false}
          placeholder="New Cube Order No"
          value={selectedCubeOrderId}
          onSearch={setSearchContent}
          notFoundContent={
            isFetchingFilteredCubeOrders ? (
              <Spin size="small" />
            ) : (
              <Alert message="No results found" type="warning" showIcon />
            )
          }
          onChange={setSelectedCubeOrderId}
        />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          type="primary"
          disabled={
            selectedCubeOrderId == null ||
            (cubeUserCode != null &&
              !selectedCubeOrderId.startsWith(cubeUserCode))
          }
          onClick={() => {
            const [cubeUserCode, cubeOrderId] = selectedCubeOrderId!.split("@");
            changeOrderAssociatedCubeId(orderId!, cubeOrderId, cubeUserCode!);
          }}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
});

export default memo(OrderModifyCube);
